import { metersToFeet } from 'utils/distanceUtil';
import { metersPerSecondToMph } from 'utils/speedUtil';

import { VerticalContainer } from '@american-autonomy/ag-ui';

import StepperField from 'components/StepperField';

import LabeledToggle from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/LabeledToggle';
import ProductCategoryField from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/SpraySettingsSection/components/ProductCategoryField';
import SpraySpecificFields from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/SpraySettingsSection/components/SpraySpecificFields';
import SpreadSpecificFields from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/SpraySettingsSection/components/SpreadSpecificFields';
import {
    APPLICATION_TYPE_OPTIONS,
    APPLICATION_TYPE_SPRAY,
    GROUND_SPEED,
    LABELS,
    OVERLAP_METERS
} from 'pages/ApplicationDetailsPage/components/FlightPlannerPanel/components/SpraySettingsSection/constants';

import useImperialHandlers from './hooks/useImperialHandlers';

import { TEST_ID } from './constants';
import type {
    ApplicationSettingsCreateRequestApplicationType,
    FunctionComponent,
    Props,
    SpraySettings
} from './types';

import styles from './styles.module.scss';

const SpraySettingsBody: FunctionComponent<Props> = ({
    className = '',
    onSpraySettingsChange,
    spraySettings,
    testId = TEST_ID
}) => {
    const isSpray = spraySettings.applicationType === APPLICATION_TYPE_SPRAY;
    const applicationTypeIndex = APPLICATION_TYPE_OPTIONS.findIndex(
        option => option.value === spraySettings.applicationType
    );
    const update = (partial: Partial<SpraySettings>) =>
        onSpraySettingsChange({ spraySettings: partial });
    const handleApplicationTypeClick = (value: string) =>
        update({ applicationType: value as ApplicationSettingsCreateRequestApplicationType });
    const { handleOverlapChange, handleSpeedChange } = useImperialHandlers({ onUpdate: update });

    return (
        <VerticalContainer className={`${className} ${styles.spraySettingsBody}`} testId={testId}>
            <LabeledToggle
                activeIndex={applicationTypeIndex}
                label={LABELS.applicationType}
                onClick={handleApplicationTypeClick}
                options={APPLICATION_TYPE_OPTIONS}
            />

            <ProductCategoryField
                label={LABELS.productCategory}
                onChange={({ productCategory }) => update({ productCategory })}
                value={spraySettings.productCategory}
            />

            <StepperField
                label={LABELS.groundSpeed}
                max={GROUND_SPEED.max}
                min={GROUND_SPEED.min}
                onChange={handleSpeedChange}
                step={GROUND_SPEED.step}
                value={metersPerSecondToMph(spraySettings.speedMetersPerSecond)}
            />

            <StepperField
                label={LABELS.passOverlap}
                max={OVERLAP_METERS.max}
                min={OVERLAP_METERS.min}
                onChange={handleOverlapChange}
                step={OVERLAP_METERS.step}
                value={metersToFeet(spraySettings.overlapMeters)}
            />

            {isSpray && (
                <SpraySpecificFields
                    nozzleRatePercentage={spraySettings.nozzleRatePercentage}
                    onChange={onSpraySettingsChange}
                    rateLitersPerHectare={spraySettings.rateLitersPerHectare}
                />
            )}

            {!isSpray && (
                <SpreadSpecificFields
                    onChange={onSpraySettingsChange}
                    rateKilogramsPerHectare={spraySettings.rateKilogramsPerHectare}
                    turntablePercentage={spraySettings.turntablePercentage}
                />
            )}
        </VerticalContainer>
    );
};

export default SpraySettingsBody;
